import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { 
  setActualDSDTableData,
  setActualDSDZoneTableData 
} from '../slices/dloSlice';

// Type definitions for better type safety
interface WeeklyDataRow {
  'Loc ID': string;
  YR: number;
  PRD: number;
  WK: number;
  day_num: number;
  override_daily_cases?: number | null;
  default_daily_cases?: number | null;
}

interface ZoneDataRow {
  ID?: number;
  'Loc ID': string;
  'Location Name'?: string;
  VP_Zone?: string;
  YR: number;
  PRD: number;
  WK: number;
  day_num: number;
  override_cases?: number | null;
  default_cases?: number | null;
  override_kcph?: number | null;
  default_kcph?: number | null;
}

// Helper function for rounding to 2 decimal places
export const round2 = (n: number): number => Math.round((n ?? 0) * 100) / 100;

// Helper function to distribute total evenly across zones with remainder handling
export function distributeEvenly(total: number, n: number): number[] {
  if (!n || n === 0) return [];
  
  const per = round2(total / n);
  const values = Array(n).fill(per);
  const sum = values.reduce((a, b) => a + b, 0);
  const remainder = round2(total - sum);
  
  // Add remainder to the last zone to preserve exact total
  if (remainder !== 0 && values.length > 0) {
    values[values.length - 1] = round2(values[values.length - 1] + remainder);
  }
  
  return values;
}

// Helper function to distribute total proportionally based on default values
export function distributeProportionally(total: number, defaultValues: number[]): number[] {
  if (!defaultValues || defaultValues.length === 0) return [];
  
  // Calculate the total of all default values
  const defaultTotal = defaultValues.reduce((sum, val) => sum + (val || 0), 0);
  
  // If no defaults or all defaults are 0, fall back to equal distribution
  if (defaultTotal === 0) {
    return distributeEvenly(total, defaultValues.length);
  }
  
  // Calculate proportional values: (defaultValue / defaultTotal) * total
  const proportionalValues = defaultValues.map(defaultVal => 
    round2((defaultVal / defaultTotal) * total)
  );
  
  // Handle rounding remainder to preserve exact total
  const sum = proportionalValues.reduce((a, b) => a + b, 0);
  const remainder = round2(total - sum);
  
  // Add remainder to the largest zone to minimize relative error
  if (remainder !== 0 && proportionalValues.length > 0) {
    const maxIndex = proportionalValues.indexOf(Math.max(...proportionalValues));
    proportionalValues[maxIndex] = round2(proportionalValues[maxIndex] + remainder);
  }
  
  return proportionalValues;
}

interface DistributeWeeklyToZonesParams {
  locId: string;
  yr: number;
  prd: number;
  wk: number;
  dayNum: number;
  overrideValue?: number; // Optional override value passed directly
}

interface RecalculateWeeklyFromZonesParams {
  locId: string;
  yr: number;
  prd: number;
  wk: number;
  dayNum: number;
}

/**
 * Distribute weekly override value to zone override values
 * Only active when in ActualVolumeTab
 */
export const distributeWeeklyOverrideToZones = createAsyncThunk(
  'actualVolume/distributeWeeklyToZones',
  async (params: DistributeWeeklyToZonesParams, { getState, dispatch }) => {
    const state = getState() as RootState;
    
    // Guard: Only sync when in ActualVolumeTab
    const isActualVolumeTab = state.dlo?.scheduleSetting?.activeContext === 'ActualVolumeTab';
    
    if (!isActualVolumeTab) {
      console.log('Skipping weekly->zone sync: not in ActualVolumeTab context');
      return;
    }

    const { locId, yr, prd, wk, dayNum, overrideValue: passedOverrideValue } = params;
    
    // Get current data from Redux state
    const weeklyData = state.dlo.scheduleSetting.scheduleSettingsActualDaily?.data || [];
    const zoneData = state.dlo.scheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data || [];
    const actualDSDZoneTableData = state.dlo.scheduleSetting.actualDSDZoneTableData || [];

    console.log('distributeWeeklyOverrideToZones - State data:', {
      weeklyDataLength: weeklyData.length,
      zoneDataLength: zoneData.length,
      tableDataLength: actualDSDZoneTableData.length,
      params
    });

    // Use passed override value or try to find from API data
    let overrideDailyCases = passedOverrideValue;
    
    if (!overrideDailyCases) {
      // Fallback to API data if no value passed
      const weeklyRow = weeklyData.find((row: WeeklyDataRow) => 
        row['Loc ID'] === locId && 
        row.YR === yr && 
        row.PRD === prd && 
        row.WK === wk && 
        row.day_num === dayNum
      );

      console.log('Found weekly row:', weeklyRow);
      
      if (weeklyRow) {
        overrideDailyCases = weeklyRow.override_daily_cases;
      }
    }

    console.log('Override daily cases for distribution:', overrideDailyCases);
    
    // Find visible zones for this day
    const visibleZones = zoneData.filter((zone: ZoneDataRow) => 
      zone['Loc ID'] === locId && 
      zone.YR === yr && 
      zone.PRD === prd && 
      zone.WK === wk && 
      zone.day_num === dayNum
    );

    if (visibleZones.length === 0) {
      console.warn('No zones found for distribution:', params);
      return;
    }

    // Allow distribution even when value is 0 to properly zero out zones
    const distributionValue = overrideDailyCases ?? 0;
    console.log(`Distributing ${distributionValue} cases (not percentages) across ${visibleZones.length} zones for day ${dayNum}`);

    // Get default values for each visible zone to distribute proportionally
    const defaultValues = visibleZones.map((zone: ZoneDataRow) => zone.default_cases || 0);
    console.log('Default values for zones:', defaultValues);
    
    // Distribute the total value proportionally based on default values
    // When distributionValue is 0, this will return an array of zeros
    const distributedValues = distributeProportionally(distributionValue, defaultValues);
    console.log('Proportionally distributed values:', distributedValues);

    // Update zone table data with distributed values
    const updatedZoneTableData = [...actualDSDZoneTableData];
    
    console.log('Before zone table update - current data length:', updatedZoneTableData.length);
    console.log('Visible zones to update:', visibleZones.length);
    console.log('Day number:', dayNum);
    
    // Map day number to day key
    const dayMapping: { [key: number]: string } = {
      1: 'sun', 2: 'mon', 3: 'tue', 4: 'wed', 5: 'thu', 6: 'fri', 7: 'sat'
    };
    
    const dayKey = dayMapping[dayNum];
    if (!dayKey) {
      console.error('Invalid day number:', dayNum);
      return;
    }
    
    console.log('Updating day:', dayKey);
    
    let updatedZones = 0;
    visibleZones.forEach((zone: ZoneDataRow, index: number) => {
      // Find matching zone in table data by VP_Zone name
      const zoneTableIndex = updatedZoneTableData.findIndex(tableZone => 
        tableZone.id === zone.VP_Zone?.toLowerCase() || 
        tableZone.zoneName === zone.VP_Zone
      );
      
      if (zoneTableIndex !== -1) {
        const distributedValue = distributedValues[index];
        console.log(`Updating zone ${zone.VP_Zone} (${updatedZoneTableData[zoneTableIndex].id}) at index ${zoneTableIndex} with value ${distributedValue}`);
        
        // Update the specific day's override value in the zone table format (as cases, not percentage)
        // Don't format to decimal here - let the table handle formatting on blur
        updatedZoneTableData[zoneTableIndex] = {
          ...updatedZoneTableData[zoneTableIndex],
          percentages: {
            ...updatedZoneTableData[zoneTableIndex].percentages,
            [dayKey]: {
              ...updatedZoneTableData[zoneTableIndex].percentages[dayKey],
              override: String(distributedValue) // Store as string without forcing decimal format
            }
          }
        };
        updatedZones++;
      } else {
        console.warn(`Zone ${zone.VP_Zone} not found in table data for update. Available zones:`, 
          updatedZoneTableData.map(z => ({ id: z.id, zoneName: z.zoneName })));
      }
    });

    console.log(`Updated ${updatedZones} zones out of ${visibleZones.length} visible zones`);
    
    // Update zone data with system source to prevent recursive updates
    console.log('Dispatching setActualDSDZoneTableData with updated data...');
    dispatch(setActualDSDZoneTableData(updatedZoneTableData));
    
    console.log('Distribution complete:', distributedValues);
    
    // Return the updated data for verification
    return {
      distributedValues,
      updatedZonesCount: updatedZones,
      totalZones: visibleZones.length
    };
  }
);

/**
 * Recalculate weekly total from zone override values
 * Only active when in ActualVolumeTab
 */
export const recalculateWeeklyFromZones = createAsyncThunk(
  'actualVolume/recalculateWeeklyFromZones',
  async (params: RecalculateWeeklyFromZonesParams, { getState, dispatch }) => {
    const state = getState() as RootState;
    
    // Guard: Only sync when in ActualVolumeTab
    const isActualVolumeTab = state.dlo?.scheduleSetting?.activeContext === 'ActualVolumeTab';
    
    if (!isActualVolumeTab) {
      console.log('Skipping zone->weekly sync: not in ActualVolumeTab context');
      return;
    }

    const { locId, yr, prd, wk, dayNum } = params;
    
    // Get current data from Redux state
    const zoneData = state.dlo.scheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data || [];
    const actualDSDZoneTableData = state.dlo.scheduleSetting.actualDSDZoneTableData || [];

    console.log('recalculateWeeklyFromZones - State data:', {
      zoneDataLength: zoneData.length,
      tableDataLength: actualDSDZoneTableData.length,
      params
    });

    // Sum zone override values from table data (more up-to-date than API)
    let totalOverride = 0;
    let zonesFound = 0;

    if (actualDSDZoneTableData.length > 0) {
      // Map day number to day key
      const dayMapping: { [key: number]: string } = {
        1: 'sun', 2: 'mon', 3: 'tue', 4: 'wed', 5: 'thu', 6: 'fri', 7: 'sat'
      };
      
      const dayKey = dayMapping[dayNum];
      if (!dayKey) {
        console.error('Invalid day number for zone recalculation:', dayNum);
        return;
      }
      
      console.log(`Calculating total from zone data for day: ${dayKey}`);
      
      // Use zone table data - sum all zones' override values for the specific day
      actualDSDZoneTableData.forEach((zone: any) => {
        console.log(`Checking zone ${zone.id || zone.zoneName}:`, zone.percentages?.[dayKey]);
        
        if (zone.percentages && zone.percentages[dayKey]) {
          const dayData = zone.percentages[dayKey];
          let overrideValue = 0;
          
          // Parse override value - for Actual Volume tab, these are cases (not percentages)
          if (dayData.override !== undefined && dayData.override !== null) {
            // Handle both string and number formats, remove any percentage symbols
            const overrideStr = String(dayData.override).replace('%', '');
            overrideValue = parseFloat(overrideStr) || 0;
          }
          
          console.log(`Zone ${zone.id || zone.zoneName} override for ${dayKey}: "${dayData.override}" -> ${overrideValue} cases`);
          
          // Include all override values, even if 0, to ensure we capture changes
          totalOverride += overrideValue;
          zonesFound++;
        } else {
          console.log(`Zone ${zone.id || zone.zoneName} missing percentages or day ${dayKey}`);
        }
      });
      
      console.log(`Found ${zonesFound} zones with override values for ${dayKey}, total: ${totalOverride} cases`);
    }

    // Fallback to API data if no table data
    if (zonesFound === 0 && zoneData.length > 0) {
      console.log('Fallback to API data for zone values');
      const visibleZones = zoneData.filter((zone: ZoneDataRow) => 
        zone['Loc ID'] === locId && 
        zone.YR === yr && 
        zone.PRD === prd && 
        zone.WK === wk && 
        zone.day_num === dayNum
      );

      visibleZones.forEach((zone: ZoneDataRow) => {
        const overrideValue = zone.override_cases || 0;
        if (overrideValue > 0) {
          totalOverride += overrideValue;
          zonesFound++;
        }
      });
    }

    console.log(`Total override cases from ${zonesFound} zones: ${totalOverride} cases`);

    if (zonesFound === 0) {
      console.warn('No zones found for recalculation:', params);
      return;
    }

    console.log(`Recalculating weekly total from zones: ${totalOverride} for day ${dayNum}`);

    // Update weekly table data
    const actualDSDTableData = state.dlo.scheduleSetting.actualDSDTableData || [];
    const updatedWeeklyTableData = [...actualDSDTableData];
    
    console.log('Weekly table data for update:', {
      dataLength: updatedWeeklyTableData.length,
      firstRow: updatedWeeklyTableData[0]
    });
    
    if (updatedWeeklyTableData.length > 0) {
      const weeklyRowIndex = 0; // Assuming single row structure
      const weeklyRow = updatedWeeklyTableData[weeklyRowIndex];
      
      console.log('Weekly row structure:', {
        hasPercentages: !!weeklyRow?.percentages,
        percentagesKeys: weeklyRow?.percentages ? Object.keys(weeklyRow.percentages) : []
      });
      
      if (weeklyRow?.percentages) {
        // Map day number to day key
        const dayMapping: { [key: number]: string } = {
          1: 'sun', 2: 'mon', 3: 'tue', 4: 'wed', 5: 'thu', 6: 'fri', 7: 'sat'
        };
        
        const dayKey = dayMapping[dayNum];
        if (dayKey && weeklyRow.percentages[dayKey]) {
          console.log(`Updating weekly ${dayKey} from ${weeklyRow.percentages[dayKey].override} cases to ${round2(totalOverride).toFixed(2)} cases`);
          
          // Update the override value for the specific day - in Actual Volume tab, these are cases
          // Don't format to decimal here - let the table handle formatting on blur
          updatedWeeklyTableData[weeklyRowIndex] = {
            ...weeklyRow,
            percentages: {
              ...weeklyRow.percentages,
              [dayKey]: {
                ...weeklyRow.percentages[dayKey],
                override: String(round2(totalOverride)) // Store as string without forcing decimal format
              }
            }
          };
          
          // Recalculate total cases for the week
          let newTotal = 0;
          Object.values(updatedWeeklyTableData[weeklyRowIndex].percentages).forEach((day: any) => {
            const overrideValue = parseFloat(day.override) || 0;
            newTotal += overrideValue;
          });
          
          updatedWeeklyTableData[weeklyRowIndex].total = String(round2(newTotal));
          
          console.log(`Weekly table updated - ${dayKey}: ${round2(totalOverride).toFixed(2)} cases, new total: ${round2(newTotal).toFixed(2)} cases`);
        } else {
          console.warn(`Cannot update weekly table - dayKey: ${dayKey}, hasDay: ${!!weeklyRow.percentages[dayKey]}`);
        }
      }
    }

    // Update weekly data with system source to prevent recursive updates
    console.log('Dispatching setActualDSDTableData with updated weekly data...');
    dispatch(setActualDSDTableData(updatedWeeklyTableData));
    
    console.log('Weekly recalculation complete:', round2(totalOverride));
    
    // Return success result
    return {
      totalOverride: round2(totalOverride),
      zonesFound,
      dayNum,
      updated: true
    };
  }
);

/**
 * Set active context for sync functionality
 */
export const setActualVolumeTabContext = createAsyncThunk(
  'actualVolume/setContext',
  async (isActive: boolean, { getState, dispatch }) => {
    // This would update a UI slice to track the active tab context
    // For now, we'll use the existing tab tracking mechanism
    console.log('ActualVolumeTab context set to:', isActive);
  }
);